"use client";

import Link from "next/link";
import { useMemo, useRef } from "react";

import { useImpressionWhenVisible } from "@/hooks/useImpressionOnce";
import { safeSessionSet } from "@/lib/browser-storage";
import { rememberLastReading } from "@/lib/last-reading";
import {
  trackSEOAgentBridgeClickedLazy,
  trackSEOAgentBridgeViewedLazy,
  trackSEOIntentCapturedLazy,
} from "@/lib/lazy-analytics-seo-bridge";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";

export type ToolResultChatBridgeCopy = {
  eyebrow: string;
  headline: string;
  subhead: string;
  cta: string;
};

/** Where every free-tool result bridge lands: the AI astrologer chat. */
const TOOL_RESULT_CHAT_BRIDGE_TARGET = "ai-astrologer";

/**
 * The chat a result bridge opens. A tool that already minted a reading (the
 * rashi calculator's POST /kundali persists one) opens THAT reading's chat, the
 * same `/chat/<id>/?lang=` the /ai-astrologer form hands off to, so the chart
 * the seeker just computed is the chart the Jyotishi answers from. A tool with
 * no reading (dasamsa, prashna) lands on the chat-first astrologer page, whose
 * form prefills the birth details the tool form saved.
 * @internal
 */
export function toolResultChatHref({
  readingId,
  sourcePath,
  language,
}: {
  readingId?: string;
  sourcePath: string;
  language: "en" | "hi";
}): string {
  if (readingId) return `/chat/${encodeURIComponent(readingId)}/?lang=${language}`;
  const config = getSEOIntentConfigForSource(sourcePath);
  return buildSEOAgentHref(
    { ...config, target: TOOL_RESULT_CHAT_BRIDGE_TARGET },
    sourcePath,
    language
  );
}

/**
 * Result-step bridge from a free tool that still ranks into the AI astrologer
 * chat, carrying what the tool computed (recovery lever 1, 2026-10-27 readout).
 *
 * It reuses the SEO agent bridge mechanism end to end:
 *  - the `seo_entry_*` session keys (the chat auto-sends `seo_entry_prompt` as
 *    the seeker's first message, so the chat opens on THIS result, not a
 *    generic greeting — the PrashnaFollowUpCTA contract);
 *  - `seo_agent_bridge_viewed` / `_clicked` + `seo_intent_captured`, tagged with
 *    this lever's `placement` (and the reading id when there is one), so each
 *    lever's clicks join to paid chat orders in the warehouse.
 *
 * Rules: 39 — a secondary navigation CTA, never a monetization offer (the
 * page's single report offer stays primary); 47 — the impression fires once,
 * only when the card is genuinely seen, on a stable string key.
 */
export function ToolResultChatBridge({
  language,
  sourcePath,
  placement,
  prompt,
  copy,
  presentationKey,
  readingId,
}: {
  language: "en" | "hi";
  /** The tool page the seeker is on, e.g. `/hi/rashi-calculator`. */
  sourcePath: string;
  /** This lever's analytics placement, e.g. `rashi_result_chat_bridge`. */
  placement: string;
  /** The first chat message, composed from the computed result. */
  prompt: string;
  copy: ToolResultChatBridgeCopy;
  /** Stable per-result identity for the one impression (rule 47). */
  presentationKey: string;
  readingId?: string;
}) {
  const rootRef = useRef<HTMLAnchorElement>(null);
  const { config, href, payload } = useMemo(() => {
    const resolved = getSEOIntentConfigForSource(sourcePath);
    return {
      config: resolved,
      href: toolResultChatHref({ readingId, sourcePath, language }),
      payload: {
        source_path: sourcePath,
        intent: resolved.intent,
        page_category: resolved.pageCategory,
        offer_type: resolved.primaryOffer,
        plan_id: resolved.reportPlanId,
        placement,
        ...(readingId ? { reading_id: readingId } : {}),
      },
    };
  }, [language, placement, readingId, sourcePath]);

  useImpressionWhenVisible(
    `seo_agent_bridge|${placement}|${presentationKey}`,
    () => trackSEOAgentBridgeViewedLazy(payload),
    rootRef
  );

  function handleClick() {
    try {
      safeSessionSet("seo_entry_intent", config.intent);
      safeSessionSet("seo_entry_path", sourcePath);
      safeSessionSet("seo_entry_page_category", config.pageCategory);
      safeSessionSet("seo_entry_primary_offer", config.primaryOffer);
      safeSessionSet("seo_entry_report_plan_id", config.reportPlanId);
      safeSessionSet("seo_entry_prompt", prompt);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // Storage unavailable: the link still opens the chat, without auto-send.
    }
    if (readingId) rememberLastReading({ id: readingId, language });
    trackSEOAgentBridgeClickedLazy({ ...payload, target: TOOL_RESULT_CHAT_BRIDGE_TARGET });
    trackSEOIntentCapturedLazy({
      source_path: sourcePath,
      intent: config.intent,
      page_category: config.pageCategory,
      target: TOOL_RESULT_CHAT_BRIDGE_TARGET,
      placement,
    });
  }

  return (
    <Link
      ref={rootRef}
      href={href}
      prefetch={false}
      onClick={handleClick}
      data-tool-result-chat-bridge={placement}
      className="mt-4 flex flex-col gap-3 rounded-xl border border-border-default bg-surface-brand-subtle p-4 no-underline transition-shadow hover:shadow-md sm:flex-row sm:items-center sm:justify-between sm:p-5"
    >
      <span className="min-w-0">
        <span className="block text-caption font-semibold uppercase tracking-wide text-intent-brand">
          {copy.eyebrow}
        </span>
        <span className="mt-1 block text-body font-semibold text-text-primary">
          {copy.headline}
        </span>
        <span className="mt-1 block text-caption text-text-secondary">{copy.subhead}</span>
      </span>
      <span className="inline-flex min-h-11 shrink-0 items-center justify-center self-start rounded-lg bg-surface-brand-strong px-4 text-button font-semibold text-text-on-brand sm:self-center">
        {copy.cta}
      </span>
    </Link>
  );
}
